import { Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { ActionSheetController, IonContent, ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { WebsiteCoverSample } from 'src/app/commons/website';
import { MediaType } from 'src/app/interfaces/general';
import { WebsiteDetail, WebsiteMedia, WebsiteMediaList, WebsiteThemeCover } from 'src/app/interfaces/website';
import { FunctionService } from 'src/app/services/general/function.service';
import { PhotoService } from 'src/app/services/general/photo.service';
import { PopupService } from 'src/app/services/general/popup.service';
import { WebsiteDetailService } from 'src/app/services/website/website-detail.service';
import { WebsiteThemeService } from 'src/app/services/website/website-theme.service';
import { WebsitePreviewComponent } from '../../website-preview/website-preview.component';
import { PrivilegeService } from 'src/app/services/account/privilege/privilege.service';

@Component({
  selector: 'app-website-cover-list',
  templateUrl: './website-cover-list.component.html',
  styleUrls: ['./website-cover-list.component.scss'],
})
export class WebsiteCoverListComponent  implements OnInit, OnDestroy {

  @ViewChild('grid', { read: ElementRef }) grid: ElementRef;
  @ViewChild('content') private content: IonContent;
  @ViewChild('content', { read: ElementRef, static: false }) contentElement: ElementRef;
  /**
   * Watch screen resize change for login slider setup
   */
  @HostListener('window:resize', ['$event'])onResize(event: any) {
    this.setupWidthHeight();
    this.resizeLimit();
  }
  /**
   * Watch screen orientation change for login slider setup
   */
  @HostListener('window:orientationchange', ['$event']) onOrientationChange(event: any) {
    this.setupWidthHeight();
    this.resizeLimit();
  }

  wizardMode: boolean;

  websiteCoverMedia: MediaType;

  selected: MediaType;

  width: number;
  height: number;

  limit: number;

  coverSampleList: WebsiteMediaList = WebsiteCoverSample;
  coverList: WebsiteMedia[] = [];
  private photoSubscription: Subscription;

  private websiteCoverSubscription: Subscription;

  constructor(
    private translate: TranslateService,
    private modalController: ModalController,
    private actionSheetController: ActionSheetController,
    private websiteDetailService: WebsiteDetailService,
    private websiteThemeService: WebsiteThemeService,
    private privilegeService: PrivilegeService,
    private photoService: PhotoService,
    private functionService: FunctionService,
    private popupService: PopupService,
  ) { }

  ngOnInit() {}

  ngOnDestroy(): void {
    this.unwatchPhoto();
    this.unwatchWebsiteCover();
  }

  ionViewWillEnter() {
    this.coverList = Object.values(this.coverSampleList).sort((a: WebsiteMedia, b: WebsiteMedia) => {
      return this.functionService.compare(a?.order, b?.order);
    });

    this.photoService.setupWebsiteCover(this.selected);
    this.setupLimit();
    this.setupWidthHeight();
    this.watchPhoto();
    this.watchWebsiteCover();
  }

  ionViewWillLeave() {
    this.unwatchPhoto();
    this.unwatchWebsiteCover();
  }

  ionViewDidLeave() {
    this.selected = null;
  }

  async watchWebsiteCover() {
    if (!this.websiteCoverSubscription) {
      this.websiteCoverSubscription = this.websiteThemeService.observable.subscribe(() => {
        const websiteCover = this.websiteThemeService.getWebsiteCover();
        if (!this.functionService.isEqual(websiteCover?.media, this.websiteCoverMedia)) {
          this.websiteCoverMedia = websiteCover.media;
        }
      });
    }
  }
  
  async unwatchWebsiteCover() {
    if (this.websiteCoverSubscription) {
      this.websiteCoverSubscription.unsubscribe();
      this.websiteCoverSubscription = null;
    }
  }

  watchPhoto() {
    if (!this.photoSubscription) {
      this.photoSubscription = this.photoService.observableWebsiteCover.subscribe((media: MediaType) => {
        if (media?.url) {
          this.selected = this.functionService.cloneDeep(media);
        }
      });
    }
  }

  unwatchPhoto() {
    if (this.photoSubscription) {
      this.photoSubscription.unsubscribe();
      this.photoSubscription = null;
    }
  }

  selectPhoto() {
    this.photoService.selectPhoto('website_cover');
  }

  async deletePhoto() {
    const confirm = await this.popupService.presentConfirm(
      this.translate.instant('CRUD.confirm_delete_field', {
        field: this.translate.instant('WEBSITE.theme.cover.photo')
    }));
    confirm.onWillDismiss().then((result: any) => {
      if (result?.data?.confirm) {
        this.photoService.deletePhoto('website_cover');
      }
    });
  }

  async setupWidthHeight(contentWidth?: number) {
    if (!contentWidth) {
      await this.functionService.delay(200);
      contentWidth = this.getContentWidth();
    }
    
    if (contentWidth) {
      this.width = this.websiteThemeService.calculatePreviewWidth(contentWidth);
      this.height = (this.width * 16) / 9;
      this.limit = this.getLimit();
    } else {
      setTimeout(() => {
        this.setupWidthHeight(contentWidth);
      }, 200);
    }
  }

  getContentWidth() {
    return this.contentElement?.nativeElement?.offsetWidth;
  }

  getContentHeight() {
    return this.contentElement?.nativeElement?.offsetHeight;
  }

  async back() {
    const websiteCover = await this.generateData();

    if (!this.functionService.isEqual(websiteCover, this.websiteThemeService.getWebsiteCover())) {
      this.confirmDiscard();
    } else {
      this.dismissModal();
    }
  } 
  
  async confirmDiscard() {
    const actionSheet = await this.actionSheetController.create({
      header: this.translate.instant('DISCARD.msg'),
      buttons: [{
        role: 'destructive',
        text: this.translate.instant('DISCARD.btn.save'),
        icon: 'checkmark-done',
        handler: () => {
          this.save(true);
        }
      }, {
        text: this.translate.instant('DISCARD.btn.discard'),
        icon: 'trash',
        handler: () => {
          this.dismissModal();
        }
      }, {
        text: this.translate.instant('BTN.cancel'),
        icon: 'close',
        role: 'cancel',
        handler: () => {
        }
      }]
    });
    actionSheet.present();
  }

  async dismissModal(coverMedia?: MediaType) {
    if (this.modalController) {
      const modal = await this.modalController.getTop();
      if (modal) { modal.dismiss({ coverMedia }); }
    }
  }

  async presentWebsitePreivewModal() {
    const websiteDetail: WebsiteDetail = this.functionService.cloneDeep(this.websiteDetailService.getWebsiteDetail());
    websiteDetail.theme.cover = await this.generateData();
    const modal = await this.modalController.create({
      component: WebsitePreviewComponent,
      cssClass: 'modal-full-screen',
      componentProps: {
        websiteDetail,
      }
    });
    modal.present();
  }

  select(cover?: MediaType) {
    if (cover?.url && cover?.type === 'local') {
      this.selected = cover;
      this.photoService.setupWebsiteCover({ ...cover });
    } else {
      this.selectPhoto();
    }
  }

  async generateData(upload?: boolean): Promise<WebsiteThemeCover> {
    const websiteCover: WebsiteThemeCover = this.functionService.cloneDeep(this.websiteThemeService.getWebsiteCover());
    if (!this.functionService.isEqual(this.selected, websiteCover?.media)) {
      websiteCover.media = this.selected;

      if (upload) {
        if (websiteCover?.media?.type === 'base64') {
          const url = await this.photoService.uploadWebsiteCover(this.selected.url);
          if (url) {
            websiteCover.media.url = url;
            websiteCover.media.type = 'firebase';
          }
        } else if (this.photoService.deleteWebsiteCoverFlag) {
          await this.photoService.deleteWebsiteCover();
        }
      }
    }

    return websiteCover;
  }

  async save(dismiss?: boolean) {
    if (this.checkPrivilege('contents')) {
      if (!this.functionService.isUndefined(this.selected)) {
        await this.popupService.presentLoading();
        const websiteCover = await this.generateData(true);
  
        if (!this.wizardMode) {
          if (!this.functionService.isEqual(websiteCover, this.websiteThemeService.getWebsiteCover())) {
            await this.websiteThemeService.saveWebsiteCover(websiteCover);
          }
        }
        await this.popupService.dismissLoading();
        await this.popupService.saveSuccessToast();
        if (dismiss) {
          this.dismissModal(websiteCover?.media);
        }
      }
    } else {
      this.popupService.presentAlert(this.translate.instant('ACCOUNT_PRIVILEGE.msg.no_privilege'));
    }
  }

  checkPrivilege(action: string): boolean {
    return this.privilegeService.checkCurrentUserPrivilege('website', action);
  }

  setupLimit() {
    const width = this.getContentWidth();
    const height = this.getContentHeight();

    if (width && height) {
      this.limit = this.getLimit();
    } else {
      setTimeout(() => {
        this.setupLimit();
      }, 300);
    }
  }

  getLimit() {
    const col = Math.floor(this.getContentWidth() / (this.width + 5));
    const row = Math.ceil(this.getContentHeight() / (this.height + 5));

    const limit = Math.floor(col * (row));
    // console.log(limit);
    return limit ? limit - 1 : 3;
  }

  async loadLimit(event: any) {
    await this.functionService.delay(50);
    if (this.limit >= this.coverList.length) {
      // event.target.disabled = true;
    } else {
      this.limit += this.getLimit();
    }
    event.target.complete();
  }

  resizeLimit() {
    const limit = this.getLimit();
    if (limit > this.limit) {
      this.limit = limit;
      this.scrollToTop();
    }
  }

  scrollToTop(time?: number) {
    if (this.content) {
      this.content.scrollToTop(time ? time : 0);
    }
  }

}
